import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      localStorage.setItem('oaa_token', res.data.token);
      localStorage.setItem('oaa_user', JSON.stringify(res.data.user));
      navigate('/audience-intelligence');
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-[380px]">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-16">
          <div className="w-8 h-8 rounded-md bg-[#0A0A0A] flex items-center justify-center">
            <span className="text-[#F66922] text-[12px] font-black">O</span>
          </div>
          <h1 className="text-[#0A0A0A] text-2xl font-bold tracking-tight">OASIS</h1>
        </div>

        {/* Form */}
        <div data-testid="login-form">
          <h2 className="text-[28px] font-bold text-[#0A0A0A] tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-neutral-400 mb-8">Enter your credentials to continue</p>

          {error && (
            <div data-testid="login-error" className="text-red-500 text-sm mb-5 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-red-500" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-[0.1em] mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@oaa.com"
                required
                className="w-full h-11 px-4 rounded-md border border-neutral-200 bg-white text-sm focus:ring-1 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none placeholder:text-neutral-300"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-[0.1em] mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full h-11 px-4 pr-11 rounded-md border border-neutral-200 bg-white text-sm focus:ring-1 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none placeholder:text-neutral-300"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-500">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0A0A0A] hover:bg-[#262626] active:scale-[0.98] text-white text-sm font-semibold tracking-wide uppercase rounded-md disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-neutral-100 text-center">
            <p className="text-[11px] text-neutral-300">Demo: admin@oaa.com / admin123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
